<template>
  <div class="ui-page" tabindex="-1">
    <AppFilterToolbar :key="activeFlowTab">
      <template #primary>
        <app-input
          v-model="filters.search"
          design="new"
          type="search"
          placeholder="Ticket, customer, email or phone"
          aria-label="Search conversations"
          @input.native="scheduleFilterReload"
        />
      </template>
      <app-select
        v-model="filters.ticket_status"
        design="new"
        :options="ticketStatusOptions"
        :reduce="option => option.value"
        label="label"
        placeholder="Ticket status"
        aria-label="Ticket status"
        :clearable="false"
        multiple
        @input="reloadForFilters"
      />
      <app-select
        v-model="filters.channel"
        design="new"
        :options="channelOptions"
        :reduce="option => option.value"
        label="label"
        multiple
        placeholder="Channels"
        aria-label="Channels"
        @input="reloadForFilters"
      >
        <template #custom-option="option">
          <div class="ui-option-copy">
            <span class="ui-ellipsis">{{ option.label }}</span>
            <small>{{ option.meta }}</small>
          </div>
        </template>
        <template #custom-selected-option="option"><span>{{ option.label }}</span></template>
      </app-select>
      <app-date-picker v-model="filters.date_from" design="new" placeholder="From" :input-attr="{ 'aria-label': 'From' }" @input="reloadForFilters" />
      <app-date-picker v-model="filters.date_to" design="new" placeholder="To" :input-attr="{ 'aria-label': 'To' }" @input="reloadForFilters" />
      <template #selection>
        <app-filters v-model="filters.state" design="new" refined weight="medium" :filters="queueStateOptions" name="communicationQueueState" aria-label="Queue" @change="reloadForFilters" />
      </template>
      <template v-if="filters.state === 'open'" #secondary-selection>
        <app-filters :value="filters.match_type || 'all'" design="new" refined weight="medium" :filters="matchFilters" name="communicationMatchType" aria-label="Matching" @change="applyMatchType($event === 'all' ? null : $event)" />
      </template>
      <template #actions><AppRefreshButton :loading="queueLoading" @click="refresh" /></template>
      <template v-if="hasActiveFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
    </AppFilterToolbar>

    <AppContentSkeleton v-if="queueLoading" class="communication-matching__table" label="Loading conversations" :columns="tableColumns" :min-width="$route.name === 'crm-trengo-matching' ? '1200px' : '1350px'" />
    <AppErrorState v-else-if="queueError" :message="queueError" @retry="loadQueue" />
    <template v-else>
      <AppCard v-if="queue.length" design="new" variant="table" header-density="compact">
        <AppDataTable class="communication-matching__table" design="new" density="dense" :rows="queue" :columns="tableColumns" :min-width="$route.name === 'crm-trengo-matching' ? '1200px' : '1350px'" clickable @row-click="openReview">
          <template #cell-conversation="{ row }">
            <AppTableText v-if="activeFlowTab === 'v2'" :text="`#${row.ticket.external_id}`" />
            <span v-else class="ui-ellipsis">#{{ row.ticket.external_id }}</span>
          </template>
          <template #cell-customer="{ row }">
            <AppTableText :text="row.contact && row.contact.name" fallback="Unknown customer" />
          </template>
          <template #cell-channel="{ row }">
            <div class="ui-cell-inline">
              <AppTableText :text="channelName(row.ticket.channel)" />
              <img v-if="isWhatsAppChannel(row.ticket.channel)" src="@/assets/img/wa-round-icon.svg" alt="" aria-hidden="true" class="ui-cell-icon">
            </div>
          </template>
          <template #cell-matching="{ row }">
            <AppStatusBadge design="new" compact :tone="matchTone(row.match_type.code)" :label="friendlyMatchName(row)" />
          </template>
          <template #cell-lead="{ row }">
            <div v-if="rowLead(row)" class="ui-cell-inline">
              <span v-if="countryFlag(rowLead(row).country)" :class="'fflag ff-sm fflag-' + countryFlag(rowLead(row).country)" aria-hidden="true" />
              <AppTableText :text="rowLead(row).name" />
              <AppStatusBadge v-if="leadState(rowLead(row))" design="new" compact class="ui-cell-badge" :color="leadState(rowLead(row)).color || ''" :label="leadState(rowLead(row)).name" />
            </div>
            <span v-else>—</span>
          </template>
          <template #cell-last_activity="{ row }">
            <AppTableText :text="compactDate(row.ticket.last_activity_at)" />
          </template>
          <template #cell-messages="{ row }">{{ row.ticket.message_count }}</template>
          <template #cell-status="{ row }">
            <AppStatusBadge design="new" compact :tone="ticketStatusTone(row.ticket.status.code)" :label="ticketStatusLabel(row.ticket.status.code, row.ticket.status.name)" />
          </template>
        </AppDataTable>
      </AppCard>
      <app-no-results v-else design="new" title="No conversations found" text="Try changing the queue, status or date filters." />
      <AppPagination v-if="queue.length" design="new" placement="embedded" :page="pagination.page" :pages="pagination.total_pages" :count="pagination.count" :visible-count="queue.length" :page-size="pagination.page_size" :loading="queueLoading" label="Conversation pages" @change="changePage" />
    </template>

    <CommunicationReviewModal
      v-if="selectedModalId"
      :key="`${activeFlowTab}:${selectedModalId}`"
      :ticket-id="selectedModalId"
      :flow-version="activeFlowTab"
      @close="closeRouteModal"
      @resolved="onReviewResolved"
      @stale="refresh"
    />
  </div>
</template>

<script>
import { format } from 'date-fns'
import {
  getCommunicationReconciliationQueue,
  getCommunicationReconciliationSummary
} from '@/http'
import CommunicationReviewModal from './ReviewModal.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppRefreshButton from '@/components/AppRefreshButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import { ticketStatusTone, ticketStatusLabel } from '@/views/crm/trengo-workspace/statusPresentation'
import { modalRoute } from '@/views/crm/modalRoute'

const DEFAULT_TICKET_STATUSES = ['OPEN', 'ASSIGNED']
const ACTIVE_TICKET_STATUSES = [...DEFAULT_TICKET_STATUSES].sort().join(',')

const EMPTY_SUMMARY = {
  open_total: 0,
  suggested: 0,
  ambiguous: 0,
  tour_operator: 0,
  no_match: 0,
  unprocessed: 0,
  ignored: 0,
  linked: 0,
  channels: []
}

export default {
  name: 'CommunicationReconciliation',
  mixins: [modalRoute('ticket')],
  components: { CommunicationReviewModal, AppContentSkeleton, AppErrorState, AppRefreshButton, AppTableText },
  props: {
    flowVersion: {
      type: String,
      required: true,
      validator: value => ['v2', 'legacy'].includes(value)
    }
  },
  data() {
    return {
      summary: { ...EMPTY_SUMMARY },
      queue: [],
      queueLoading: false,
      queueError: '',
      pagination: {
        page: 1,
        page_size: 20,
        count: 0,
        total_pages: 0
      },
      filters: {
        search: '',
        state: 'open',
        date_from: '',
        date_to: '',
        match_type: null,
        ticket_status: [...DEFAULT_TICKET_STATUSES],
        channel: []
      },
      filterTimer: null,
      queueRequestId: 0,
      summaryRequestId: 0
    }
  },
  computed: {
    activeFlowTab() {
      return this.flowVersion
    },
    tableColumns() {
      return [
        { key: 'conversation', label: 'Conversation', width: '10%' },
        { key: 'customer', label: 'Customer', width: '13%' },
        { key: 'channel', label: 'Channel', width: '11%' },
        { key: 'matching', label: 'Matching', width: '14%' },
        { key: 'lead', label: this.leadColumnTitle, width: '20%' },
        { key: 'last_activity', label: 'Last activity', width: '14%' },
        { key: 'messages', label: 'Messages', width: '8%', align: 'right' },
        { key: 'status', label: 'Status', width: '10%' }
      ]
    },
    matchFilters() {
      return [
        { value: 'all', label: `All (${this.summary.open_total})` },
        { value: 'suggested', label: `Suggested (${this.summary.suggested})` },
        { value: 'ambiguous', label: `Ambiguous (${this.summary.ambiguous})` },
        { value: 'tour_operator', label: `Tour Operator (${this.summary.tour_operator})` },
        { value: 'no_match', label: `No match (${this.summary.no_match})` }
      ]
    },
    ticketStatusOptions() {
      return [
        { value: 'OPEN', label: 'Open' },
        { value: 'ASSIGNED', label: 'Assigned' },
        { value: 'CLOSED', label: 'Closed' },
        { value: 'INVALID', label: 'Invalid' }
      ]
    },
    channelOptions() {
      return (this.summary.channels || []).map(channel => ({
        value: channel.external_id,
        label: this.channelName(channel),
        meta: this.channelMeta(channel)
      }))
    },
    queueStateOptions() {
      return [
        { value: 'open', label: `Open (${this.summary.open_total})` },
        { value: 'ignored', label: `Ignored (${this.summary.ignored})` },
        { value: 'linked', label: `Linked (${this.summary.linked})` }
      ]
    },
    leadColumnTitle() {
      return this.filters.state === 'linked' ? 'Linked lead' : 'Suggested lead'
    },
    hasActiveFilters() {
      return Boolean(
        this.filters.search
        || this.filters.state !== 'open'
        || this.filters.date_from
        || this.filters.date_to
        || this.filters.match_type
        || this.filters.channel.length
        || this.normalizedTicketStatuses !== ACTIVE_TICKET_STATUSES
      )
    },
    normalizedTicketStatuses() {
      return [...this.filters.ticket_status].sort().join(',')
    }
  },
  watch: {
    activeFlowTab: {
      immediate: true,
      handler() {
        window.clearTimeout(this.filterTimer)
        this.pagination.page = 1
        this.queue = []
        this.summary = { ...EMPTY_SUMMARY }
        this.refresh()
      }
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.filterTimer)
    this.queueRequestId += 1
    this.summaryRequestId += 1
  },
  methods: {
    cleanParams(extra = {}) {
      const params = { ...this.filters, ...extra }
      params.search = params.search ? params.search.trim() : ''
      if (Array.isArray(params.ticket_status)) {
        params.ticket_status = params.ticket_status.join(',')
      }
      if (Array.isArray(params.channel)) {
        params.channel = params.channel.join(',')
      }
      return Object.keys(params).reduce((result, key) => {
        if (params[key] !== '' && params[key] !== null && params[key] !== undefined) result[key] = params[key]
        return result
      }, {})
    },
    async refresh() {
      await Promise.all([
        this.loadSummary(),
        this.loadQueue()
      ])
    },
    async loadSummary() {
      const flowVersion = this.activeFlowTab
      const requestId = ++this.summaryRequestId
      try {
        const { data } = await getCommunicationReconciliationSummary(flowVersion, this.cleanParams({ match_type: undefined, state: undefined }))
        if (requestId !== this.summaryRequestId || flowVersion !== this.activeFlowTab) return false
        this.summary = data
        return true
      } catch (error) {
        if (requestId !== this.summaryRequestId || flowVersion !== this.activeFlowTab) return false
        this.notifyError(error, 'Unable to load matching summary')
        return false
      }
    },
    async loadQueue() {
      const flowVersion = this.activeFlowTab
      const requestId = ++this.queueRequestId
      this.queueLoading = true
      this.queueError = ''
      try {
        const { data } = await getCommunicationReconciliationQueue(flowVersion, this.cleanParams({
          page: this.pagination.page,
          page_size: this.pagination.page_size
        }))
        if (requestId !== this.queueRequestId || flowVersion !== this.activeFlowTab) return false
        this.queue = data.results
        this.pagination.page = data.page
        this.pagination.page_size = data.page_size
        this.pagination.count = data.count
        this.pagination.total_pages = data.total_pages
        return true
      } catch (error) {
        if (requestId !== this.queueRequestId || flowVersion !== this.activeFlowTab) return false
        this.queueError = this.errorMessage(error, 'Unable to load conversations')
        return false
      } finally {
        if (requestId === this.queueRequestId) this.queueLoading = false
      }
    },
    scheduleFilterReload() {
      window.clearTimeout(this.filterTimer)
      this.filterTimer = window.setTimeout(this.reloadForFilters, 350)
    },
    reloadForFilters() {
      window.clearTimeout(this.filterTimer)
      if (!this.filters.ticket_status.length) this.filters.ticket_status = [...DEFAULT_TICKET_STATUSES]
      if (this.filters.state !== 'open') this.filters.match_type = null
      this.pagination.page = 1
      this.loadQueue()
      this.loadSummary()
    },
    clearFilters() {
      this.filters = {
        search: '',
        state: 'open',
        date_from: '',
        date_to: '',
        match_type: null,
        ticket_status: [...DEFAULT_TICKET_STATUSES],
        channel: []
      }
      this.reloadForFilters()
    },
    applyMatchType(code) {
      if (code) this.filters.state = 'open'
      this.filters.match_type = code
      this.reloadForFilters()
    },
    changePage(page) {
      this.pagination.page = page
      this.loadQueue()
      this.$nextTick(() => {
        const table = this.$el.querySelector('.communication-matching__table')
        if (table) table.scrollIntoView({ block: 'start' })
      })
    },
    openReview(payload) {
      const row = payload && payload.row ? payload.row : payload
      const event = payload && payload.event
      const trigger = event && event.target && event.target.closest('tr')
      if (row && row.ticket_id) this.openRouteModal(row.ticket_id, trigger)
    },
    onReviewResolved() {
      this.refresh()
    },
    friendlyMatchName(row) {
      if (row.match_type.code === 'ambiguous') return `${ row.candidate_count } possible leads`
      return row.match_type.name
    },
    matchTone(code) {
      return {
        suggested: 'green',
        ambiguous: 'amber',
        tour_operator: 'amber',
        linked: 'blue',
        ignored: 'neutral',
        no_match: 'red',
        rejected: 'red',
        unprocessed: 'red'
      }[code] || 'neutral'
    },
    leadState(lead) {
      if (!lead) return null
      if (this.activeFlowTab === 'v2') return lead.stage || { name: 'Stage not set' }
      return lead.status
    },
    rowLead(row) {
      return row.linked_lead || row.suggested_lead || null
    },
    channelCode(channel) {
      return String((channel && (channel.code || channel.channel_type || channel.type)) || '').toUpperCase()
    },
    channelName(channel) {
      if (!channel) return 'Unknown channel'
      if (channel.display_name || channel.title) {
        return channel.display_name || channel.title
      }
      const name = channel.name || channel.channel_type || channel.type || channel.code
      if (!name) return 'Unknown channel'
      const externalId = channel.external_id
      return externalId ? `${ name } · #${ externalId }` : name
    },
    channelMeta(channel) {
      if (!channel) return ''
      const parts = []
      const type = channel.channel_type || channel.type || channel.code
      if (type) parts.push(String(type).replace(/_/g, ' '))
      if (channel.is_private === true) parts.push('Private')
      if (channel.is_private === false) parts.push('Not private')
      if (channel.is_private === null || channel.is_private === undefined) parts.push('Privacy not synced')
      return parts.join(' · ')
    },
    isWhatsAppChannel(channel) {
      const code = this.channelCode(channel)
      const name = this.channelName(channel)
      return code.includes('WHATSAPP')
        || code.startsWith('WA_')
        || /whats\s*app|wa_business/i.test(name)
    },
    ticketStatusTone,
    ticketStatusLabel,
    countryFlag(country) {
      const code = country && country.code ? String(country.code) : ''
      return /^[a-z]{2}$/i.test(code) ? code : ''
    },
    compactDate(value) {
      return value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'
    },
    exactDate(value) {
      return this.compactDate(value)
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      const detail = data && data.detail
      if (data && data.message) return data.message
      if (data && typeof data.details === 'string') return data.details
      if (detail && typeof detail === 'object') return detail.message || detail.detail || fallback
      if (typeof detail === 'string') return detail
      if (data && typeof data === 'object') {
        const fieldError = Object.entries(data).find(([key, value]) => {
          return !['code', 'message', 'detail'].includes(key) && value
        })
        if (fieldError) {
          const [field, value] = fieldError
          const message = Array.isArray(value) ? value.join(' ') : value
          return `${ field.replace(/_/g, ' ') }: ${ message }`
        }
      }
      return fallback
    },
    notifyError(error, fallback) {
      this.$notify({ type: 'error', title: this.errorMessage(error, fallback) })
    }
  }
}
</script>
